import { useEffect, useRef, useState } from 'react'

import { easeInOutQuad } from '~/shared/helpers/animationFunctions'
import { useProgressAnimation } from '~/shared/hooks/useProgressAnimation'

const SLOW_NUMBER_INTERVAL = 1000 / 8

interface IParams {
  halfDuration: number
  midpoint?: number
  onMidpoint?: VoidFunction
  onComplete?: VoidFunction
}

export const useSegmentAnimation = ({
  halfDuration,
  midpoint = 50,
  onMidpoint,
  onComplete,
}: IParams) => {
  const midpointFired = useRef(false)
  const onMidpointRef = useRef(onMidpoint)
  onMidpointRef.current = onMidpoint

  const firstDuration = Math.round((halfDuration * midpoint) / 50)
  const secondDuration = Math.round((halfDuration * (100 - midpoint)) / 50)

  const {
    counter: c1,
    isFinished: f1,
    progressAnimationPlayback: playFirst,
  } = useProgressAnimation({
    duration: firstDuration,
    to: midpoint,
    animationFunction: easeInOutQuad,
  })

  const {
    counter: c2,
    isFinished: f2,
    progressAnimationPlayback: playSecond,
  } = useProgressAnimation({
    duration: secondDuration,
    to: 100 - midpoint,
    animationFunction: easeInOutQuad,
    callback: onComplete,
  })

  const playSecondRef = useRef(playSecond)
  playSecondRef.current = playSecond

  useEffect(() => {
    if (f1 && !midpointFired.current) {
      midpointFired.current = true
      if (onMidpointRef.current) {
        onMidpointRef.current()
      } else {
        playSecondRef.current()
      }
    }
  }, [f1])

  const displayValue = f1 ? midpoint + c2 : c1
  const displayValueRef = useRef(0)
  displayValueRef.current = displayValue

  const [slowValue, setSlowValue] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setSlowValue(displayValueRef.current)
    }, SLOW_NUMBER_INTERVAL)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (f2) setSlowValue(100)
  }, [f2])

  return {
    displayValue,
    slowValue,
    isFinished: f2,
    start: playFirst,
    resume: playSecond,
  }
}
